var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Intl
import { setupIntl } from '@jetshop/intl/config';
import { NetworkStatusProvider } from '@jetshop/ui/Loading/NetworkStatusContext';
// Sentry
import * as Sentry from '@sentry/browser';
// Emotion
import { hydrate } from 'emotion';
import React from 'react';
// Cookies
import { Cookies } from 'react-cookie';
// React DOM
import { hydrate as renderToDOM } from 'react-dom';
// Metadata
// React Loadable
import Loadable from 'react-loadable';
// React Router DOM
import { BrowserRouter } from 'react-router-dom';
import { ChannelHandler } from '../../ChannelHandler/ChannelHandler';
import { getSelectedAndRecommendedChannel } from '../../ChannelHandler/channelUtils';
import { CartIdProvider } from '../../components/Cart/CartIdContext';
import logWarningInDev from '../../helpers/logWarningInDev';
import { getActiveFiltersFromSearch } from '../../resolvers/filters/filterParams';
import { createClient } from '../apollo';
import { AuthHandler } from '../AuthHandler';
import channelsQuery from '../ChannelsQuery.gql';
import { PreviewHandler } from '../PreviewHandler';
import SharedTree from '../SharedTree';
import createIntlOptions from '../utils/createIntOptions';
import { setUID } from '../utils/uid';
// Instantiate this here, since we need to read cookies outside of React.
const cookies = new Cookies();
function getInitialCacheData() {
    return {
        data: {
            activeFilters: getActiveFiltersFromSearch(document.location.search)
        }
    };
}
const startClient = (tree, config) => {
    if (!config.apolloConfig.channelsQuery) {
        logWarningInDev(true, `No channelsQuery was provided in the apolloConfig, which is a deprecated behaviour. Please update your shop.config.js to include the ChannelsQuery to avoid breaking in the next major release. See https://gitlab.jetshop.se/jetshop/flight/blob/master/packages/template-trend/src/ChannelsQuery.gql for a reference implementation`);
    }
    // This is used by cypress to get the shopid when running universal cypress test
    window.shopid = config.apolloConfig.shopid;
    Sentry.init({
        dsn: config.sentryClientDSN,
        ignoreErrors: [
            'PersistedQueryNotFound',
            /^(.*?(\bMissingRoute\b)[^$]*)/g,
            'PERSISTED_QUERY_NOT_FOUND'
        ]
    });
    Sentry.configureScope(scope => {
        scope.setTag('BUILD_TARGET', 'client');
    });
    const { apolloConfig } = config;
    const channelHandler = new ChannelHandler();
    const authHandler = new AuthHandler({
        cookies
    });
    const previewHandler = new PreviewHandler();
    // Read locale from cookie and make sure we've loaded translations before we start rendering
    const { client, NetworkStatusNotifier } = createClient({
        apolloConfig,
        channelHandler,
        authHandler,
        previewHandler
    });
    const initialCacheData = getInitialCacheData();
    // Initialise cache with active filters from the search params
    client.cache.writeData(initialCacheData);
    // If cache is reseted, we reset the initial cache data
    client.onResetStore(() => __awaiter(void 0, void 0, void 0, function* () { return client.cache.writeData(initialCacheData); }));
    client
        .query({
        query: config.apolloConfig.channelsQuery || channelsQuery
    })
        .then(({ data }) => {
        const channels = data.channels;
        const geoipCountry = window.__GEOIP_COUNTRY__;
        const acceptedLanguages = JSON.parse(window.__ACCEPTED_LANGUAGES__);
        const { selectedChannel, recommendedChannel } = getSelectedAndRecommendedChannel({
            url: window.location.href,
            cookies,
            channels,
            singleDomainMode: config.singleDomainMode,
            geoipCountry,
            acceptedLanguages
        });
        channelHandler.setSelectedChannel(selectedChannel);
        channelHandler.setRecommendedChannel(recommendedChannel);
        channelHandler.setChannels(channels);
        const channelOverrides = config.channelOverrides && selectedChannel.name
            ? config.channelOverrides[selectedChannel.name]
            : {};
        const { intl } = Object.assign(Object.assign({}, config), channelOverrides);
        const intlOptions = createIntlOptions({
            selectedChannel,
            options: intl.options
        });
        return setupIntl({
            translations: intl.translations,
            locale: selectedChannel.language.culture,
            defaultLocale: intl.defaultLocale,
            options: intlOptions
        }).then(() => {
            const activeBreakpoint = window.__ACTIVE_BREAKPOINT__;
            const emotionIds = window.__EMOTION_IDS__;
            emotionIds && hydrate(emotionIds);
            const releware = {
                enabled: config.relewareEnabled,
                baseUrl: document.domain,
                uid: config.relewareEnabled ? setUID(cookies) : null
            };
            const wrappedTree = (React.createElement(SharedTree, { activeBreakpoint: activeBreakpoint, config: config, cookies: cookies, releware: releware, client: client, Router: BrowserRouter, authHandler: authHandler, channelHandler: channelHandler, previewHandler: previewHandler },
                React.createElement(NetworkStatusNotifier, { render: (value) => {
                        return (React.createElement(CartIdProvider, null,
                            React.createElement(NetworkStatusProvider, { value: value }, tree)));
                    } })));
            return Loadable.preloadReady().then(() => wrappedTree);
        });
    })
        .then((tree) => {
        renderToDOM(tree, document.getElementById('root'));
    });
};
const main = (tree, config) => {
    // If the polyFillsLoaded flag is set to true then we know that the polyFills have been loaded and we can start the client.
    if (window.polyFillsLoaded) {
        return startClient(tree, config);
    }
    else {
        // If the polyfills have not been loaded we want to replace the bootShop to start the client instead so that once the polyfills are loaded, bootShop will start the client.
        window.bootShop = () => startClient(tree, config);
    }
};
export default main;
//# sourceMappingURL=startClient.js.map